import { useEffect, useRef, useState } from "react";
import { Eye, Pencil, Sparkles, X } from "lucide-react";
import { ConfirmDialog } from "./Dialog.tsx";
import { isMarkdownPath, MarkdownPreview } from "./MarkdownPreview.tsx";
import { monaco } from "../lib/monaco.ts";
import { handleEditorWheel } from "../lib/editorContainment.ts";
import { loadEditorSettings, saveEditorSettings } from "../lib/editorSettings.ts";
import { useRelay } from "../lib/relayContext.tsx";
import {
  registerAutocompleteProvider,
  setAutocompleteStatus,
  subscribeAutocompleteStatus,
} from "../lib/autocomplete/autocompleteProvider.ts";
import type { OpenFiles } from "../lib/useOpenFiles.ts";

const TAB_MIME = "application/x-racler-tab";

/** Global unsaved-changes bar, derived from the open-files dirty flags (single source of truth). */
function UnsavedBar({ files }: { files: OpenFiles }) {
  const [listOpen, setListOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const dirty = files.files.filter((f) => f.dirty);
  const failed = dirty.filter((f) => f.error).length;
  const saving = files.files.some((f) => f.saving);
  useEffect(() => {
    if (dirty.length === 0) setListOpen(false);
  }, [dirty.length]);
  if (dirty.length === 0) return null;
  const n = dirty.length;
  const label = `${n} unsaved ${n === 1 ? "file" : "files"}`;
  return (
    <div className="unsaved-bar" role="region" aria-label="Unsaved changes" data-testid="unsaved-bar">
      <div className="unsaved-summary">
        <button
          type="button"
          className="unsaved-count"
          aria-expanded={listOpen}
          aria-haspopup="true"
          onClick={() => setListOpen((o) => !o)}
          data-testid="unsaved-count"
        >
          {label}
        </button>
        {failed > 0 && <span className="error small">{failed} failed to save</span>}
        {listOpen && (
          <ul
            className="unsaved-list"
            aria-label="Unsaved files"
            onKeyDown={(e) => e.key === "Escape" && setListOpen(false)}
          >
            {dirty.map((f) => (
              <li key={f.path}>
                <button
                  type="button"
                  title={f.path}
                  onClick={() => {
                    files.activate(f.path);
                    setListOpen(false);
                  }}
                  data-testid={`unsaved-item-${f.path}`}
                >
                  {f.path}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button type="button" disabled={saving} onClick={() => void files.saveAll()} data-testid="save-all">
        Save All
      </button>
      <button type="button" onClick={() => setConfirming(true)} data-testid="discard-all">
        Discard All
      </button>
      {confirming && (
        <ConfirmDialog
          title="Discard unsaved changes?"
          message={`This will discard changes in ${n} unsaved ${n === 1 ? "file" : "files"}.`}
          confirmLabel="Discard"
          danger
          onConfirm={() => files.discardAll()}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

/** Tabs + a single Monaco editor instance whose model is swapped per tab (view state is kept per file). */
export function EditorArea({ files }: { files: OpenFiles }) {
  const host = useRef<HTMLDivElement>(null);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const viewStates = useRef(new Map<string, monaco.editor.ICodeEditorViewState | null>());
  const relay = useRelay();
  const [autocompleteEnabled, setAutocompleteEnabled] = useState(() => loadEditorSettings().autocomplete);
  const [autocompleteUnavailable, setAutocompleteUnavailable] = useState<string | null>(null);

  const setSelectionRef = useRef(files.setSelection);
  setSelectionRef.current = files.setSelection;
  const saveRef = useRef(files.save);
  saveRef.current = files.save;
  const formatRef = useRef(files.format);
  formatRef.current = files.format;
  const active = files.files.find((f) => f.path === files.activePath) ?? null;
  const shownPath = useRef<string | null>(null);

  // Markdown files open rendered; paths in `editing` show the source instead.
  const [editing, setEditing] = useState<ReadonlySet<string>>(new Set());
  const seenMd = useRef(new Set<string>());
  const activeMdPath = active !== null && isMarkdownPath(active.path) ? active.path : null;
  const activeMdEmpty = active !== null && active.model.getValueLength() === 0;
  // A new/empty Markdown file starts in source mode so you can type right away; others start rendered.
  useEffect(() => {
    if (activeMdPath === null || seenMd.current.has(activeMdPath)) return;
    seenMd.current.add(activeMdPath);
    if (activeMdEmpty) setEditing((prev) => new Set(prev).add(activeMdPath));
  }, [activeMdPath, activeMdEmpty]);
  const isMd = active !== null && isMarkdownPath(active.path);
  const previewing = isMd && !editing.has(active.path);
  const togglePreview = (path: string) =>
    setEditing((prev) => {
      const next = new Set(prev);
      if (!next.delete(path)) next.add(path);
      return next;
    });

  useEffect(() => {
    return subscribeAutocompleteStatus((s) => {
      setAutocompleteEnabled(s.enabled);
      setAutocompleteUnavailable(s.unavailableReason);
    });
  }, []);

  const toggleAutocomplete = () => {
    const settings = loadEditorSettings();
    const next = !settings.autocomplete;
    saveEditorSettings({ ...settings, autocomplete: next });
    setAutocompleteEnabled(next);
    setAutocompleteStatus({ enabled: next });
  };

  // Tab drag-and-drop reordering state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{ index: number; position: "before" | "after" } | null>(
    null,
  );

  useEffect(() => {
    if (!host.current) return;
    registerAutocompleteProvider(relay);

    const editor = monaco.editor.create(host.current, {
      model: null,
      theme: "racler-dark",
      automaticLayout: true,
      fontSize: 13,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      tabSize: 2,
      autoIndent: "full",
      formatOnPaste: false, // We control format-on-paste via listener to agent
      scrollbar: {
        alwaysConsumeMouseWheel: true,
      },
      inlineSuggest: {
        enabled: true,
        mode: "prefix",
        suppressSuggestions: false,
      },
    });
    // Cmd/Ctrl+S saves the active file (instead of the browser's "save page").
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => void saveRef.current());

    // Format Document shortcut: Shift+Option+F (macOS) / Shift+Alt+F (Windows/Linux)
    editor.addAction({
      id: "racler-format-document",
      label: "Format Document",
      keybindings: [monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF],
      run: () => void formatRef.current(),
    });

    // Handle Format on Paste
    let pasteDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    editor.onDidPaste(() => {
      const settings = loadEditorSettings();
      if (!settings.formatOnPaste) return;
      if (pasteDebounceTimer) clearTimeout(pasteDebounceTimer);
      pasteDebounceTimer = setTimeout(() => {
        void formatRef.current();
      }, 150);
    });
    editor.onDidChangeCursorSelection(() => {
      const sel = editor.getSelection();
      const model = editor.getModel();
      if (!sel || !model || sel.isEmpty() || !shownPath.current) {
        setSelectionRef.current(null);
        return;
      }
      const text = model.getValueInRange(sel);
      if (!text) {
        setSelectionRef.current(null);
        return;
      }
      setSelectionRef.current({
        path: shownPath.current,
        startLine: sel.startLineNumber,
        startColumn: sel.startColumn,
        endLine: sel.endLineNumber,
        endColumn: sel.endColumn,
        selectedText: text,
      });
    });
    editorRef.current = editor;

    // Monaco's find-widget buttons (esp. the close "X") show a pointer tooltip that lands on top of the button,
    // steals the pointer and makes the button unclickable (hover flicker loop). Monaco registers the tooltip via
    // a `mouseover` listener on each button, so swallow that event in the capture phase for the find widget.
    // The buttons keep their CSS hover state and aria-labels; only the tooltip is suppressed.
    const hostEl = host.current;
    const suppressFindWidgetTooltip = (e: MouseEvent) => {
      if (e.target instanceof Element && e.target.closest(".find-widget")) e.stopPropagation();
    };
    hostEl.addEventListener("mouseover", suppressFindWidgetTooltip, true);

    return () => {
      hostEl.removeEventListener("mouseover", suppressFindWidgetTooltip, true);
      editor.dispose();
    };
  }, [relay]);

  // Intercept horizontal trackpad scrolling on the editor host to prevent macOS Back/Forward navigation.
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    el.addEventListener("wheel", handleEditorWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleEditorWheel);
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (shownPath.current) viewStates.current.set(shownPath.current, editor.saveViewState());
    editor.setModel(active?.model ?? null);
    if (active) {
      const vs = viewStates.current.get(active.path);
      if (vs) editor.restoreViewState(vs);
      if (!previewing) editor.focus();
    }
    shownPath.current = active?.path ?? null;
  }, [active, previewing]);

  // Go to Definition / Problems panel: select the requested range once the right file is showing, then focus.
  const { pendingReveal, clearReveal } = files;
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !pendingReveal || active?.path !== pendingReveal.path) return;
    if (editor.getModel() !== active.model) return;
    editor.setSelection(pendingReveal.range);
    editor.revealRangeInCenterIfOutsideViewport(pendingReveal.range);
    editor.focus();
    clearReveal();
  }, [pendingReveal, active, clearReveal]);

  return (
    <div className="editor-area">
      <div className="tabs" role="tablist">
        {files.files.map((f, index) => {
          const isDragging = draggedIndex === index;
          const isTarget = dropIndicator?.index === index;
          const dropClass = isTarget
            ? dropIndicator.position === "before"
              ? "drop-before"
              : "drop-after"
            : "";
          return (
            <div
              key={f.path}
              role="tab"
              draggable={true}
              className={`tab ${f.path === files.activePath ? "active" : ""} ${isDragging ? "dragging" : ""} ${dropClass}`.trim()}
              onClick={() => files.activate(f.path)}
              onAuxClick={(e) => e.button === 1 && files.close(f.path)}
              onDragStart={(e) => {
                e.dataTransfer.setData(TAB_MIME, String(index));
                e.dataTransfer.setData("text/plain", f.path);
                e.dataTransfer.effectAllowed = "move";
                setDraggedIndex(index);
              }}
              onDragEnd={() => {
                setDraggedIndex(null);
                setDropIndicator(null);
              }}
              onDragOver={(e) => {
                if (!e.dataTransfer.types.includes(TAB_MIME)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                const rect = e.currentTarget.getBoundingClientRect();
                const mid = rect.left + rect.width / 2;
                const position = e.clientX < mid ? "before" : "after";
                setDropIndicator({ index, position });
              }}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                setDropIndicator((prev) => (prev?.index === index ? null : prev));
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (!e.dataTransfer.types.includes(TAB_MIME)) return;
                const raw = e.dataTransfer.getData(TAB_MIME);
                const sourceIndex = raw !== "" ? Number(raw) : draggedIndex;
                const indicator = dropIndicator;
                setDraggedIndex(null);
                setDropIndicator(null);
                if (sourceIndex === null || isNaN(sourceIndex) || sourceIndex === index) return;

                let targetIndex = index;
                if (indicator?.position === "after" && sourceIndex < index) {
                  targetIndex = index;
                } else if (indicator?.position === "after" && sourceIndex > index) {
                  targetIndex = index + 1;
                } else if (indicator?.position === "before" && sourceIndex < index) {
                  targetIndex = index - 1;
                } else if (indicator?.position === "before" && sourceIndex > index) {
                  targetIndex = index;
                }
                targetIndex = Math.max(0, Math.min(files.files.length - 1, targetIndex));
                if (targetIndex !== sourceIndex) {
                  files.reorder(sourceIndex, targetIndex);
                }
              }}
              title={f.path}
              data-testid={`tab-${f.path}`}
            >
              <span>{f.path.split("/").pop()}</span>
              {f.saving ? (
                <span className="muted"> …</span>
              ) : (
                f.dirty && (
                  <span className="dirty" data-testid="dirty">
                    ●
                  </span>
                )
              )}
              <button
                className="tab-close"
                aria-label={`Close ${f.path}`}
                onClick={(e) => {
                  e.stopPropagation();
                  files.close(f.path);
                }}
              >
                <X size={13} aria-hidden />
              </button>
            </div>
          );
        })}
        {active && (
          <>
            {isMd && (
              <button
                className="tab-save"
                onClick={() => togglePreview(active.path)}
                title={previewing ? "Edit Markdown source" : "Show rendered Markdown"}
                data-testid="toggle-md-preview"
              >
                {previewing ? <Pencil size={13} aria-hidden /> : <Eye size={13} aria-hidden />}
                {previewing ? " Edit" : " Preview"}
              </button>
            )}
            <div className="tab-fixed-action">
              <button
                className={`tab-save ai-toggle ${
                  autocompleteUnavailable ? "unavailable" : autocompleteEnabled ? "on" : "off"
                }`}
                aria-pressed={autocompleteEnabled && !autocompleteUnavailable}
                onClick={toggleAutocomplete}
                title={
                  autocompleteUnavailable
                    ? `AI Autocomplete: ${autocompleteUnavailable}`
                    : autocompleteEnabled
                      ? "AI Autocomplete: Enabled (Click to disable)"
                      : "AI Autocomplete: Disabled (Click to enable)"
                }
                data-testid="toggle-autocomplete"
              >
                <Sparkles size={13} aria-hidden />
                {autocompleteUnavailable ? "AI (Unavailable)" : autocompleteEnabled ? "AI: On" : "AI: Off"}
              </button>
            </div>
          </>
        )}
      </div>
      {(files.loadError ?? active?.error) && (
        <div className="error banner">{active?.error ?? files.loadError}</div>
      )}
      {previewing && active && <MarkdownPreview model={active.model} />}
      <div
        className="monaco-host"
        ref={host}
        data-testid="editor"
        style={previewing ? { display: "none" } : undefined}
      />
      {isMd && active && (
        <button
          type="button"
          className="md-fab"
          onClick={() => togglePreview(active.path)}
          title={previewing ? "Edit Markdown source" : "Show rendered Markdown"}
          aria-label={previewing ? "Edit Markdown source" : "Show rendered Markdown"}
          data-testid="md-fab"
        >
          {previewing ? <Pencil size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
        </button>
      )}
      <UnsavedBar files={files} />
      {!active && <div className="editor-empty muted">Open a file from the explorer.</div>}
    </div>
  );
}
